import React, { useCallback, lazy, Suspense } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import * as routes from '#app/containers/modalRoutes';
import {
  locationToCloseModal,
  locationToCloseSettings,
} from '#app/containers/routes';
import ModalLayout from '#components/Layouts/Modal/ModalLayout/ModalLayout';
import { useJWTRefreshCheck } from '#modules/utils/jwt';
import { ModalContext } from '#modules/context/ModalContext';

// Lazy load modal components to reduce initial bundle size
const UserSettings = lazy(
  () => import('#components/Modals/UserSettings/UserSettings'),
);
const NewRelationship = lazy(
  () => import('#components/Modals/NewRelationship/NewRelationship'),
);
const ReceiveInstruments = lazy(
  () =>
    import(
      '#components/Modals/InstrumentTransfers/ReceiveInstruments/ReceiveInstruments'
    ),
);
const DepositCrypto = lazy(
  () =>
    import(
      '#components/Modals/InstrumentTransfers/DepositCrypto/DepositCrypto'
    ),
);
const NewTransfer = lazy(
  () =>
    import('#components/Modals/InstrumentTransfers/NewTransfer/NewTransfer'),
);
const NewLockboxTransfer = lazy(
  () =>
    import(
      '#components/Modals/InstrumentTransfers/NewLockboxTransfer/NewLockboxTransfer'
    ),
);
const BSVTransfer = lazy(
  () =>
    import('#components/Modals/InstrumentTransfers/BSVTransfer/BSVTransfer'),
);
const NewTrade = lazy(
  () => import('#components/Modals/InstrumentTransfers/NewTrade/NewTrade'),
);
const TopupBSV = lazy(
  () => import('#components/Modals/InstrumentTransfers/TopupBSV/TopupBSV'),
);
const NewTransferRequest = lazy(
  () =>
    import(
      '#components/Modals/InstrumentTransfers/NewTransferRequest/NewTransferRequest'
    ),
);
const ActivityDetails = lazy(
  () => import('#components/Modals/ActivityDetails/ActivityDetails'),
);
const NewDeposit = lazy(
  () => import('#components/Modals/BankAccounts/NewBankDeposit/NewDeposit'),
);
const BankTransferDetails = lazy(
  () =>
    import(
      '#components/Modals/BankAccounts/BankTransferDetails/BankTransferDetails'
    ),
);
const NewBankAccount = lazy(
  () => import('#components/Modals/BankAccounts/AddBankAccount/NewBankAccount'),
);
const NewLockbox = lazy(
  () => import('#components/Modals/Lockboxes/NewLockbox'),
);
const DeleteLockbox = lazy(
  () => import('#components/Modals/Lockboxes/DeleteLockbox'),
);
const LinkAccountDialog = lazy(
  () =>
    import('#components/Modals/BankAccounts/LinkBankAccount/LinkAccountDialog'),
);
const BankAccountRemove = lazy(
  () =>
    import(
      '#components/Modals/BankAccounts/BankAccountRemove/BankAccountRemove'
    ),
);
const BuyOrder = lazy(
  () => import('#components/Modals/Assets/BuyOrder/BuyOrder'),
);
const SellOrder = lazy(
  () => import('#components/Modals/Assets/SellOrder/SellOrder'),
);
const BankAccountVerify = lazy(
  () =>
    import(
      '#components/Modals/BankAccounts/BankAccountVerify/BankAccountVerify'
    ),
);
const AddNewPlan = lazy(
  () => import('#components/Modals/AddNewPlan/AddNewPlan'),
);
const AppSettings = lazy(
  () => import('#components/Layouts/Site/AppSettings/AppSettings'),
);
const EditProfileDetails = lazy(
  () => import('#components/Modals/EditProfileDetails/EditProfileDetails'),
);
const EditEntityHandle = lazy(
  () => import('#components/Modals/EditEntityHandle/EditEntityHandle'),
);
const EditDisplayName = lazy(
  () => import('#components/Modals/EditDisplayName/EditDisplayName'),
);
const NewGiftCard = lazy(
  () => import('#components/Modals/GiftCard/NewGiftCard'),
);
const GiftCardPreview = lazy(
  () => import('#components/Modals/GiftCard/GiftCardPreview'),
);
const DepositInstrumentSelector = lazy(
  () =>
    import(
      '#components/Modals/InstrumentTransfers/DepositInstrumentSelector/DepositInstrumentSelector'
    ),
);

let modalRoutesMap = {
  [routes.WALLET_SEND]: NewTransfer,
  [routes.WALLET_TRANSFER]: NewLockboxTransfer,
  [routes.WALLET_BSV_TRANSFER]: BSVTransfer,
  [routes.WALLET_SELL]: SellOrder,
  [routes.WALLET_BUY]: BuyOrder,

  [routes.WALLET_RECEIVE_REQUEST]: NewTransferRequest,
  [routes.WALLET_EXCHANGE_OFFER]: NewTrade,
  [routes.WALLET_RECEIVE]: ReceiveInstruments,
  [routes.WALLET_DEPOSIT_CRYPTO]: DepositCrypto,
  [routes.WALLET_DEPOSIT_INSTRUMENTS]: DepositInstrumentSelector,
  [routes.WALLET_TOPUP]: TopupBSV,
  [routes.ACTIVITY_DETAILS]: ActivityDetails,
  [routes.RELATIONSHIPS_NEW]: NewRelationship,
  [routes.APP_SETTINGS]: AppSettings,
  [routes.EDIT_PROFILE_DETAILS]: EditProfileDetails,
  [routes.EDIT_ENTITY_HANDLE]: EditEntityHandle,
  [routes.EDIT_DISPLAY_NAME]: EditDisplayName,
  [routes.WALLET_LINK_ACCOUNT]: LinkAccountDialog,
  [routes.WALLET_REMOVE_ACCOUNT]: BankAccountRemove,
  [routes.WALLET_BANK_ACCOUNT_VERIFY]: BankAccountVerify,
  [routes.ADD_NEW_PLAN]: AddNewPlan,
  [routes.WALLET_NEW_BANK_ACCOUNT]: NewBankAccount,
  [routes.WALLET_NEW_LOCKBOX]: NewLockbox,
  [routes.WALLET_DELETE_LOCKBOX]: DeleteLockbox,
  [routes.WALLET_NEW_DEPOSIT]: NewDeposit,
  [routes.WALLET_NEW_WITHDRAWAL]: SellOrder,

  [routes.GIFT_CARD_NEW]: NewGiftCard,
  [routes.GIFT_CARD_PREVIEW]: GiftCardPreview,

  // Legacy Tokenized banking
  // [routes.WALLET_NEW_BANK_DEPOSIT]: BankDeposit,
  // [routes.WALLET_NEW_BANK_WITHDRAWAL]: BankWithdrawal,

  [routes.WALLET_BANK_TRANSFER_DETAILS]: BankTransferDetails,
};

function ModalLoader() {
  return null;
}

function AppModals({
  test,
  forceModalContent,
  allowBackgroundInteraction,
  showAppVersionOverForcedModal,
  maxHeightModal,
}) {
  const location = useLocation();
  const history = useHistory();
  useJWTRefreshCheck();
  const { onCloseCallback, setOnCloseCallback } =
    React.useContext(ModalContext);

  const { settings, modal } = queryString.parse(location.search) || {};

  const [modalName, modalParameters] = (modal || '').split('?');

  const ModalComponent = modalRoutesMap[modalName];

  const onCloseSettings = useCallback(() => {
    if (onCloseCallback) {
      onCloseCallback();
      setOnCloseCallback(null);
    }
    history.push(locationToCloseSettings(location));
  }, [history, location, onCloseCallback, setOnCloseCallback]);
  const onCloseModal = useCallback(() => {
    if (onCloseCallback) {
      onCloseCallback();
      setOnCloseCallback(null);
    }
    history.push(locationToCloseModal(location));
  }, [history, location, onCloseCallback, setOnCloseCallback]);

  if (forceModalContent) {
    return (
      <div className="modals">
        <ModalLayout
          test={test}
          onCloseModal={onCloseModal}
          allowBackgroundInteraction={allowBackgroundInteraction}
          showAppVersion={showAppVersionOverForcedModal}
          maxHeightModal={maxHeightModal}
        >
          {forceModalContent}
        </ModalLayout>
      </div>
    );
  }

  return (
    <Suspense fallback={<ModalLoader />}>
      <div className="modals">
        {!!settings && (
          <ModalLayout
            test={test}
            fullscreen
            ModalComponent={UserSettings}
            onCloseModal={onCloseSettings}
            maxHeightModal={maxHeightModal}
          />
        )}
        {!!ModalComponent && (
          <ModalLayout
            test={test}
            ModalComponent={ModalComponent}
            modalParameters={modalParameters}
            onCloseModal={onCloseModal}
            allowBackgroundInteraction={allowBackgroundInteraction}
            maxHeightModal={maxHeightModal}
          />
        )}
      </div>
    </Suspense>
  );
}

export default AppModals;
